import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { addSection, deleteSection, getAppConfig, upsertAppConfig } from '../../store/appConfigSlice';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { IconDeviceFloppy, IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { SortableSection } from './SortableSection';

export default function AppConfig() {
    let [dirty, setDirty] = useState(false);
    let [editingSectionIndex, setEditingSectionIndex] = useState(null);
    let [modalOpen, setModalOpen] = useState(false);
    let [isNewSection, setIsNewSection] = useState(false);
    let config = useSelector(state => state.appConfig.config);
    let dispatch = useDispatch();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            sections: config?.sections ?? [],
        },
        validate: values => {
            const errors = {};
            if (!values.sections || values.sections.length === 0) {
                errors.sections = 'Required';
            }
            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            dispatch(upsertAppConfig(values));
            setIsNewSection(false);
            setModalOpen(false);
            setEditingSectionIndex(null);
        }
    });

    useEffect(() => {
        dispatch(getAppConfig());
    }, [dispatch]);

    useEffect(() => {
        if ((formik.initialValues.sections?.length ?? 0) > 0) {
            if (formik.initialValues.sections !== formik.values.sections) {
                setDirty(true);
            } else {
                setDirty(false);
            }
        }
    }, [formik.values, formik.initialValues.sections]);

    useEffect(() => {
        formik.setFieldValue("sections", config?.sections);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, formik.setFieldValue]);

    const onSave = (e) => {
        e.preventDefault();
        formik.submitForm();
    };

    const addNewSection = (e) => {
        e.preventDefault();
        setDirty(true);
        // Add a new section (empty fields)
        const newSection = { id: Date.now().toString(), name: '', description: '', dataType: '', condition: '', display: '' };
        formik.setFieldValue('sections', [...formik.values.sections, newSection]);
        setEditingSectionIndex(formik.values.sections.length);
        setIsNewSection(true);
        setModalOpen(true);
    };

    const deleteExistingSection = (index) => {
        dispatch(deleteSection(index));
        setDirty(true);
    };

    const handleEdit = (index) => {
        setEditingSectionIndex(index);
        setIsNewSection(false);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (isNewSection) {
            // Remove the last section if canceling a new section
            const updatedSections = formik.values.sections.slice(0, -1);
            formik.setFieldValue('sections', updatedSections);
        }
        setIsNewSection(false);
        setModalOpen(false);
        setEditingSectionIndex(null);
    };

    return (
        <Card className="w-full max-w-4xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <Button onClick={onSave} className="ml-auto flex items-center gap-2" disabled={formik.isSubmitting || !dirty}>
                    <IconDeviceFloppy className="h-4 w-4" />
                    Save
                </Button>
            </CardHeader>
            <CardContent className="space-y-4">
                <form>
                    <div className="space-y-2">
                        {formik.values.sections && formik.values.sections.length > 0 ? (
                            formik.values.sections.map((section, index) => (
                                <div key={section.id} className="flex items-center justify-between border rounded p-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold truncate">{section.name || 'Untitled Section'}</div>
                                        <div className="text-sm text-muted-foreground truncate">{section.description || ''}</div>
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <Button size="sm" variant="outline" onClick={e => { e.preventDefault(); handleEdit(index); }}>
                                            <IconEdit className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="destructive" onClick={e => { e.preventDefault(); deleteExistingSection(index); }}>
                                            <IconTrash className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-muted-foreground">No sections available.</div>
                        )}
                    </div>
                </form>
                <div className="flex justify-start pt-4">
                    <Button onClick={addNewSection} variant="outline" className="flex items-center gap-2">
                        <IconPlus className="h-4 w-4" />
                        Add New Section
                    </Button>
                </div>
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent>
                        <div className="text-lg font-semibold mb-4">
                            {editingSectionIndex !== null && formik.values.sections[editingSectionIndex] ? 'Edit Section' : 'Create Section'}
                        </div>
                        {editingSectionIndex !== null && (
                            <SortableSection
                                section={formik.values.sections[editingSectionIndex]}
                                index={editingSectionIndex}
                                formik={formik}
                                removeItem={deleteExistingSection}
                            />
                        )}
                        <div className="flex justify-end gap-2 mt-6">
                            <Button onClick={handleCloseModal} variant="outline">Close</Button>
                            <Button onClick={formik.submitForm} variant="default">Save</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}